class MiniCart extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = document.querySelector('sticky-header');
    this.drawer = document.querySelector('cart-drawer');

    // Keep cart drawer mutations self-contained. The full cart page has its own
    // <cart-items> implementation, while the drawer is available site-wide.
    if (!this.cartUpdateListenersBound) {
      this.cartUpdateListenersBound = true;
      this.pendingQuantityUpdates = new Map();
      this.cartUpdateQueue = Promise.resolve();
      this.addEventListener('change', this.onQuantityChange.bind(this), true);
      this.addEventListener('click', this.onRemoveClick.bind(this), true);
    }

    new IntersectionObserver(this.handleIntersection.bind(this)).observe(this);
  }

  onQuantityChange(event) {
    const input = event.target;
    if (!input?.matches?.('input[name="updates[]"][data-index]')) return;

    // Prevent the full-cart handler from processing drawer controls on /cart.
    event.stopPropagation();

    const line = Number.parseInt(input.dataset.index, 10);
    const quantity = Number.parseInt(input.value, 10);
    if (!Number.isInteger(line) || line < 1 || !Number.isInteger(quantity) || quantity < 0) return;

    window.clearTimeout(this.pendingQuantityUpdates.get(line));
    this.pendingQuantityUpdates.set(line, window.setTimeout(() => {
      this.pendingQuantityUpdates.delete(line);
      this.enqueueCartUpdate(line, quantity);
    }, 150));
  }

  onRemoveClick(event) {
    const removeButton = event.target?.closest?.('cart-remove-button[data-index]');
    if (!removeButton || !this.contains(removeButton)) return;

    // Capture the click before an optional full-cart custom element can handle it.
    event.preventDefault();
    event.stopPropagation();

    const line = Number.parseInt(removeButton.dataset.index, 10);
    if (!Number.isInteger(line) || line < 1) return;

    window.clearTimeout(this.pendingQuantityUpdates.get(line));
    this.pendingQuantityUpdates.delete(line);
    this.enqueueCartUpdate(line, 0);
  }

  enqueueCartUpdate(line, quantity) {
    this.cartUpdateQueue = this.cartUpdateQueue
      .catch(() => undefined)
      .then(() => this.updateQuantity(line, quantity));
  }

  async updateQuantity(line, quantity) {
    this.setUpdatingState(line, true);

    try {
      const sections = this.getSectionsToRender().map((section) => section.section);
      const body = JSON.stringify({
        line,
        quantity,
        sections,
        sections_url: window.location.pathname
      });
      const response = await fetch(window.routes.cart_change_url, {
        ...fetchConfig('javascript'),
        body
      });
      const parsedState = await response.json();

      if (!response.ok || parsedState.status) {
        throw new Error(parsedState.description || `Cart update failed: ${response.status}`);
      }
      if (!parsedState.sections || typeof parsedState.sections['mini-cart'] !== 'string') {
        throw new Error('Cart drawer section was not returned');
      }

      this.renderContents(parsedState);
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cart: parsedState }
      }));
    }
    catch (error) {
      console.error(error);
      await this.refreshContents();
      this.showCartError(line, error.message);
    }
    finally {
      this.setUpdatingState(line, false);
    }
  }

  async refreshContents() {
    if (!this.dataset.url) return;

    try {
      const response = await fetch(this.dataset.url, { cache: 'no-store' });
      if (!response.ok) throw new Error(`Cart drawer refresh failed: ${response.status}`);
      const html = await response.text();
      document.getElementById('mini-cart').innerHTML =
        this.getSectionInnerHTML(html, '.shopify-section');
    }
    catch (error) {
      console.error(error);
    }
  }

  showCartError(line, message) {
    const errorText = this.querySelector(`#Line-item-error-${line} .cart-item__error-text`);
    if (errorText) errorText.textContent = message || window.cartStrings?.error || 'Cart update failed';
  }

  setUpdatingState(line, isUpdating) {
    this.toggleAttribute('aria-busy', isUpdating);
    const cartItems = this.querySelector('cart-items');
    if (cartItems) cartItems.toggleAttribute('inert', isUpdating);

    const quantityInput = this.querySelector(`input[name="updates[]"][data-index="${line}"]`);
    const lineItem = quantityInput?.closest('li') || this.querySelector(`cart-remove-button[data-index="${line}"]`)?.closest('li');
    lineItem?.querySelector('.loading-overlay')?.classList.toggle('hidden', !isUpdating);
  }

  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;
    observer.unobserve(this);

    fetch(this.dataset.url)
      .then(response => response.text())
      .then(html => {
        document.getElementById('mini-cart').innerHTML =
          this.getSectionInnerHTML(html, '.shopify-section');
          
          document.dispatchEvent(new CustomEvent('cartdrawer:opened'));
      })
      .catch(e => {
        console.error(e);
      });
  }

  open() {
    const detailsElement = this.drawer.querySelector('details');
    if (detailsElement.hasAttribute('open')) {
      return;
    }
    
    this.drawer.openMenuDrawer();
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      if (document.getElementById(section.id)) {
        document.getElementById(section.id).innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
    }));

    this.open();
  }
  // 删掉了这部分
  // {
  //   id: 'cart-icon-bubble',
  //   section: 'cart-icon-bubble',
  //   selector: '.shopify-section'
  // },
  getSectionsToRender() {
    return [
      {
        id: 'mini-cart',
        section: 'mini-cart',
        selector: '.shopify-section'
      },

      {
        id: 'mobile-cart-icon-bubble',
        section: 'mobile-cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }
}

customElements.define('mini-cart', MiniCart);
